import { useMemo, useState } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, Search, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { renderCell } from "@/components/common/cell";
import { get, titleCase } from "@/lib/format";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 12;

export function DataTable({ rows, columns, searchKeys, filters, onRowClick, onExport, exporting, testIdPrefix = "row" }) {
  const [q, setQ] = useState("");
  const [filterVals, setFilterVals] = useState({});
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [page, setPage] = useState(1);

  const filterOptions = useMemo(() => {
    const map = {};
    (filters || []).forEach((f) => {
      const set = new Set();
      (rows || []).forEach((r) => {
        const v = get(r, f.key);
        if (v !== null && v !== undefined && v !== "") set.add(String(v));
      });
      map[f.key] = Array.from(set).sort();
    });
    return map;
  }, [rows, filters]);

  const processed = useMemo(() => {
    let out = [...(rows || [])];
    if (q.trim()) {
      const needle = q.toLowerCase();
      const keys = searchKeys && searchKeys.length ? searchKeys : columns.map((c) => c.key);
      out = out.filter((r) => keys.some((k) => String(get(r, k) ?? "").toLowerCase().includes(needle)));
    }
    Object.entries(filterVals).forEach(([k, v]) => {
      if (v && v !== "__all__") out = out.filter((r) => String(get(r, k)) === v);
    });
    if (sort.key) {
      out.sort((a, b) => {
        const av = get(a, sort.key);
        const bv = get(b, sort.key);
        if (av === bv) return 0;
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        const na = Number(av), nb = Number(bv);
        let cmp;
        if (!Number.isNaN(na) && !Number.isNaN(nb)) cmp = na - nb;
        else cmp = String(av).localeCompare(String(bv));
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, q, filterVals, sort, columns, searchKeys]);

  const totalPages = Math.max(1, Math.ceil(processed.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = processed.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <Input
              data-testid="table-search"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search…"
              className="h-9 w-56 pl-8 text-sm"
            />
          </div>
          {(filters || []).map((f) => (
            <Select
              key={f.key}
              value={filterVals[f.key] || "__all__"}
              onValueChange={(v) => { setFilterVals((s) => ({ ...s, [f.key]: v })); setPage(1); }}
            >
              <SelectTrigger data-testid={`filter-${f.key}`} className="h-9 w-40 text-sm">
                <SelectValue placeholder={f.label} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="__all__">All {f.label}</SelectItem>
                {(filterOptions[f.key] || []).map((opt) => (
                  <SelectItem key={opt} value={opt}>{titleCase(opt)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{processed.length} record{processed.length === 1 ? "" : "s"}</span>
          {onExport && (
            <Button data-testid="btn-export" onClick={onExport} disabled={exporting} variant="outline" className="h-9 gap-2 text-sm">
              <Download className="h-4 w-4" /> {exporting ? "Exporting…" : "Export"}
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-stone-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200">
                {columns.map((col) => {
                  const active = sort.key === col.key;
                  const SortIcon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                  return (
                    <th
                      key={col.key}
                      data-testid={`col-${col.key}`}
                      onClick={() => toggleSort(col.key)}
                      className="cursor-pointer select-none whitespace-nowrap px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-stone-500 hover:text-amber-800"
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        <SortIcon className={cn("h-3 w-3", active ? "text-amber-700" : "text-stone-300")} />
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {pageRows.map((row, idx) => (
                <tr
                  key={idx}
                  data-testid={`${testIdPrefix}-${idx}`}
                  onClick={() => onRowClick && onRowClick(row)}
                  className="cursor-pointer transition-colors hover:bg-amber-50/50"
                >
                  {columns.map((col) => (
                    <td key={col.key} className="whitespace-nowrap px-4 py-2.5 text-xs sm:text-sm">
                      {renderCell(row, col)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50/60 px-4 py-2.5">
          <span className="text-xs text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button data-testid="btn-prev-page" variant="outline" size="sm" className="h-8" disabled={currentPage <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button data-testid="btn-next-page" variant="outline" size="sm" className="h-8" disabled={currentPage >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
